import {
  VStack,
  Box,
  Switch,
  HStack,
  Spacer,
  Text,
  Checkbox,
  Select,
} from '@chakra-ui/react'
import { FunctionComponent, useEffect, useState } from 'react'
import { IMesh, IVariant } from '../../../type/modelType'
import { variantToMeshConfiguration } from '../../../utils/modelEngine/configuration'
import {
  deleteProductVariant,
  upsertProductVariant,
} from '../../../utils/modelEngine/product'

interface IProps {
  mesh: IMesh
  setMesh: React.Dispatch<React.SetStateAction<IMesh | null>>
}

const VariantConfig: FunctionComponent<IProps> = ({ mesh, setMesh }) => {
  const [allow, setAllow] = useState<boolean>(
    mesh.variants.some(v => v.isOption)
  )

  useEffect(() => {
    if (mesh) {
      setAllow(mesh.variants.some(v => v.isOption))
    }
  }, [mesh])

  return (
    <VStack spacing="4" alignItems="left">
      <Box>
        <Switch
          size="lg"
          colorScheme="brand"
          onChange={e => {
            setAllow(e.target.checked)
            if (!e.target.checked) {
              if (mesh.meshVariantId && mesh.productId) {
                deleteProductVariant(mesh.meshVariantId, mesh.productId)
              }
              const variantId = mesh.variants.find(v => v.default)?.id
              setMesh({
                ...mesh,
                variants: [
                  ...mesh.variants.map(v => ({
                    ...v,
                    isOption: false,
                    default: !!variantId && v.id === variantId,
                  })),
                ],
              })
            }
          }}
          isChecked={allow}
        />{' '}
        Allow user to change
      </Box>

      {allow && (
        <>
          <VStack spacing="2" alignItems="flex-start">
            {mesh.variants
              .sort((a, b) => (a.meshName > b.meshName ? 1 : -1))
              .map(v => (
                <HStack w="100%">
                  <HStack spacing="2">
                    <Text>{v.meshName}</Text>
                  </HStack>

                  <Spacer />
                  <Switch
                    size="md"
                    colorScheme="brand"
                    isChecked={!!v.isOption}
                    onChange={e => {
                      const newMeshConfig = variantToMeshConfiguration(
                        { ...v, isOption: e.target.checked },
                        mesh
                      )

                      if (mesh.meshVariantId && mesh.productId) {
                        if (!newMeshConfig.variants.some(v => v.isOption)) {
                          deleteProductVariant(
                            mesh.meshVariantId,
                            mesh.productId
                          )
                        } else {
                          upsertProductVariant(
                            mesh.meshVariantId,
                            newMeshConfig.variants.filter(v => v.isOption),
                            mesh.productId
                          )
                        }
                      }

                      setMesh(newMeshConfig)
                    }}
                  />
                  <Checkbox
                    colorScheme="brand"
                    isChecked={!!v.default}
                    onChange={e => {
                      const newMeshConfig = variantToMeshConfiguration(
                        { ...v, default: e.target.checked },
                        mesh
                      )

                      setMesh(newMeshConfig)
                    }}
                  ></Checkbox>
                </HStack>
              ))}
            <Checkbox
              colorScheme="brand"
              isChecked={!mesh.canHideMesh}
              onChange={e => {
                setMesh({ ...mesh, canHideMesh: !e.target.checked })
              }}
            >
              Required
            </Checkbox>
          </VStack>
        </>
      )}

      {!allow && (
        <Select
          placeholder="Select Default Variant"
          onChange={e => {
            const variantConfig = mesh.variants.find(
              v => v.id === e.target.value
            ) as IVariant
            const newMeshConfig = variantToMeshConfiguration(
              { ...variantConfig, default: true },
              mesh
            )
            setMesh(newMeshConfig)
          }}
          value={mesh.variants?.find(t => t.default)?.id ?? ''}
        >
          {mesh.variants.map(v => (
            <option value={v.id} selected={!!v.default}>
              {v.title}
            </option>
          ))}
        </Select>
      )}
    </VStack>
  )
}

export default VariantConfig
